import React, {
    useEffect,
    useRef,
    useState
} from 'react';

import PropTypes from 'prop-types';

import RIQButton from '../../../components/RIQButton.jsx';
import RIQPillInput from '../../../components/RIQPillInput.jsx';

import {
    useCurationConfigValue,
    useCurationConfigState,
    useSetCurationConfigState,
    useGetFocusedInstanceIdState
} from '../../../hooks/widgetConfiguration.js';

import useMetrics from '../../../hooks/useMetrics.js';

import {
    useVerifySearch
} from '../hooks/useVerifySearch.js';

import {
    useLoadLandscapeListeningQuery,
    useGetListeningQueryDisplayName
} from '../hooks/useListeningQueryPersistence.js';

import useListeningCurationFilterValues from '../hooks/useListeningCurationFilterValues.js';

import useListeningSearchTracking from '../hooks/useListeningSearchTracking.js';

import useShowNBQUpsell from '../hooks/useShowNBQUpsell.js';
import useGetFilterSelectedIds from '../hooks/useGetFilterSelectedIds.js';

import ListeningMultiSelect from './ListeningMultiSelect.jsx';

import AdvancedSearchDropdown from './AdvancedSearchButton.jsx';

import QueryValidationError from './QueryValidationError.jsx';

import LanguageDropdown from './LanguageDropdown.jsx';

import {
    useGetDashboardType,
    DashboardTypes
} from '../hooks/useGetDashboardType.js';

import {
    RIQInfoPopover
} from '../../../components/RIQPopover.jsx';

import {
    Menu
} from '@headlessui/react';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

import useGetSources from '../hooks/useGetSources.js';


const {
    disabledTooltip,
    availableInQuid,
    sentimentsFromTheme
} = listeningDefinitions['toolbar-dropdown'];

const ListeningSentiments = RivalIQ.metrics.new.enums.ListeningSentiments;

Toolbar.propTypes = {
    className: PropTypes.string
};

function updateFilterProp(selectedIds, enums, stateSetterFcn) {
    if (_.isEmpty(selectedIds)) {
        stateSetterFcn(undefined);
    } else {
        const allOptions = _.keys(enums).sort();
        selectedIds = selectedIds.sort();
        if (_.isEqual(allOptions, selectedIds)) {
            stateSetterFcn(null);
        } else {
            stateSetterFcn(selectedIds.join(','));
        }
    }
}

function makeDomainMetricRequest(dimension, metricFilters, filterToOmit) {

    const updatedFilters = _.cloneDeep(metricFilters);
    delete updatedFilters.listening[filterToOmit];

    return {
        dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
        dimensions: [dimension],
        measure: 'count',
        filters: updatedFilters
    };
}

function SavedSearchSearchTermsAnd({setSearchWithinError, setSearchWithinWarning, trackingId}) {
    const listeningQueryId = useCurationConfigValue('listeningQueryId');
    const dashboardType = useGetDashboardType();
    const [searchTermsAnd, setSearchTermsAnd] = useCurationConfigState('searchTermsAnd');
    const [uncommitedSearchTermsAnd, setUncommitedSearchTermsAnd] = useState(_.isEmpty(searchTermsAnd) ? [] : searchTermsAnd[0]);

    const {data: savedSearch} = useLoadLandscapeListeningQuery(listeningQueryId);

    const {queryValidationErrors} = useVerifySearch(true, {
        searchTermsAnd: [uncommitedSearchTermsAnd]
    });

    const includeTermsError = _.findLast(queryValidationErrors?.searchTermsAnd?.[0], {
        isFatal: true
    });
    const includeTermsWarning = _.findLast(queryValidationErrors?.searchTermsAnd?.[0], {
        isFatal: false
    });

    useEffect(() => {
        const hasValidationErrors = Boolean(includeTermsError);

        // Saved searches act a little differently than regular searches - we want to kick off a page refresh immediately when a new pill is added.
        if (!_.isEqual(searchTermsAnd, [uncommitedSearchTermsAnd]) && !hasValidationErrors) {

            setSearchWithinError(null);
            setSearchTermsAnd([uncommitedSearchTermsAnd]);
        } else {
            setSearchWithinError(includeTermsError);
        }

        setSearchWithinWarning(includeTermsWarning ? includeTermsWarning : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includeTermsError?.idx, includeTermsError?.message, searchTermsAnd, setSearchTermsAnd, setSearchWithinError, setSearchWithinWarning, uncommitedSearchTermsAnd]);

    function onSearchTermsChanged(newSearchTerms) {
        RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.ListeningSearchWithin, {
            term: newSearchTerms,
            listeningQueryId: listeningQueryId,
            dashboardType: dashboardType
        });

        if (_.isFunction(newSearchTerms)) {
            newSearchTerms = newSearchTerms(searchTermsAnd);
        }

        setUncommitedSearchTermsAnd(newSearchTerms);
    }

    const searchDisplayName = useGetListeningQueryDisplayName(savedSearch, listeningQueryId);
    const placeHolder = searchDisplayName && `Search within ${searchDisplayName}`;

    return (
        <RIQPillInput
            className='tlw-w-full md_tlw-w-[300px]'
            placeHolder={placeHolder}
            pills={uncommitedSearchTermsAnd}
            setPills={onSearchTermsChanged}
            inputUniqueId='includeTermsInput'
            focusedPillIdx={includeTermsError?.idx}
            trackingId={trackingId}
        />
    );
}

function SentimentMultiSelect({sentiment, onSentimentOptionsSelected, trackingId}) {
    const listeningQueryId = useCurationConfigValue('listeningQueryId');

    const metricRequests = {};

    if (listeningQueryId) {
        metricRequests.themeSentiments = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.QUID_TOPICS,
            dimensions: ['listeningQueryId', 'themeId'],
            measure: 'themeSentiments',
            filters: {
                listening: {
                    listeningQueryId: Number(listeningQueryId)
                }
            }
        };
    }

    const {data: metricResults} = useMetrics(metricRequests);

    let currentValue = sentiment;
    let onlyEnableOptions;
    let tooltipText;

    const themeSentiments = _(metricResults?.themeSentiments?.data)
        .map(_.last)
        .filter()
        .flatMap(sentiment => sentiment.split(',').map(_.trim))
        .uniq()
        .value();

    if (_.size(themeSentiments)) {
        currentValue = themeSentiments;
        onlyEnableOptions = themeSentiments;
        tooltipText = sentimentsFromTheme;
    }

    return (
        <ListeningMultiSelect
            label='Sentiment'
            allEnumValues={ListeningSentiments}
            currentValue={currentValue}
            onItemSelectionsCommited={onSentimentOptionsSelected}
            onlyEnableOptions={onlyEnableOptions}
            tooltipText={tooltipText}
            trackingId={trackingId}
        />
    );
}

export default function Toolbar({className}) {

    const [focusedInstanceId, setFocusedInstanceId] = useGetFocusedInstanceIdState();

    const inputRef = useRef();

    useEffect(() => {
        // This effect will focus the input field when first shown.
        inputRef?.current && inputRef.current.focus();
    }, []);

    const listeningQueryId = useCurationConfigValue('listeningQueryId');

    const setSelectedSource = useSetCurationConfigState('selectedSource');

    const [sentiment, setSentiment] = useCurationConfigState('sentiment');

    const [language, setLanguage] = useCurationConfigState('language');

    const listeningSearchTracking = useListeningSearchTracking();

    const metricFilters = useListeningCurationFilterValues();

    const {data: listeningQuery} = useLoadLandscapeListeningQuery(listeningQueryId);

    const listeningQueryTypeId = listeningQuery?.listeningQueryTypeId;

    // showedUpsellInitially is necessary because when you close the upsell, we shouldn't reflow the dropdown. Instead,
    // we should keep the width of that dropdown the same.
    const [showNBQUpsell, dismissNBQUpsell] = useShowNBQUpsell();
    const [showedUpsellInitially] = useState(showNBQUpsell);

    const [searchWithinError, setSearchWithinError] = useState();
    const [searchWithinWarning, setSearchWithinWarning] = useState();

    const {allSelectableIds: allSelectableSourceIds, currentlySelectedIds: selectedSourceIds} = useGetFilterSelectedIds('source');
    const defaultLanguage = listeningQuery ? listeningQuery.definition?.language : language;

    const dashboardType = useGetDashboardType();

    const applicableSources = useGetSources();

    function onBackButtonClick() {
        setFocusedInstanceId(undefined);
    }

    function onSourceOptionsSelected(selectedIds) {
        updateFilterProp(selectedIds, applicableSources, setSelectedSource);

        listeningSearchTracking({
            selectedSource: selectedIds
        });
    }

    function onSentimentOptionsSelected(selectedIds) {
        updateFilterProp(selectedIds, ListeningSentiments, setSentiment);

        listeningSearchTracking({
            sentiment: selectedIds
        });
    }

    function onLanguageSelected(languageId) {
        setLanguage(languageId);
        listeningSearchTracking({
            language: languageId
        });
    }

    function onNBUpsellDismiss(e) {
        dismissNBQUpsell();
        e.stopPropagation();
    }

    const sourcesOtherItems = (<>
        <Menu.Item key='availableWithQuid'>
            <div
                className={`tlw-m-5 tlw-mb-0 tlw-mt-3 tlw-flex tlw-flex-row tlw-border-none tlw-bg-gray-125 tlw-py-2.5 tlw-pl-2.5 tlw-pr-2.5 tlw-text-left tlw-text-11 tlw-text-gray-650 ${!showNBQUpsell ? 'tlw-invisible tlw-my-0 tlw-h-0' : ''}`}
            >
                <div
                    className='tlw-grow'
                >
                    <span
                        className='tlw-text-sm tlw-font-semibold tlw-uppercase'
                    >
                        Only available in Quid
                    </span>
                    <RIQInfoPopover
                        description={availableInQuid}
                    />
                    <div
                        className=''
                    >
                        <span className='fa fa-facebook' />&nbsp;
                        <span className='fa fa-instagram' />&nbsp;
                        <span className='fa fab fa-tiktok' />&nbsp;
                        and more
                    </div>
                </div>

                <button
                    className='tlw-ml-5 tlw-w-11 tlw-border-0 tlw-bg-transparent tlw-bg-none tlw-p-0'
                    onClick={onNBUpsellDismiss}
                >
                    <span className='fa fa-fw fa-times tlw-text-sm' />
                </button>
            </div>
        </Menu.Item>
    </>);

    return (
        <div className={`${className} tlw-flex tlw-items-center`}>

            {focusedInstanceId &&
                <RIQButton
                    className='tlw-mr-8 tlw-self-start tlw-p-3'
                    onClick={onBackButtonClick}>
                    <span className='fa fa-chevron-left'/>
                </RIQButton>
            }

            <div
                className='tlw-flex tlw-w-full tlw-flex-col tlw-align-top'
            >
                <div className='tlw-flex tlw-w-full tlw-flex-col tlw-gap-8 sm_tlw-w-auto sm_tlw-flex-row'>
                    {dashboardType === DashboardTypes.INSTANT_SEARCH &&
                        <AdvancedSearchDropdown />
                    }
                    {_.includes([DashboardTypes.SAVED_SEARCH, DashboardTypes.TOPIC], dashboardType) &&
                        <SavedSearchSearchTermsAnd
                            setSearchWithinError={setSearchWithinError}
                            setSearchWithinWarning={setSearchWithinWarning}
                            trackingId='listening-search-within'
                        />
                    }

                    <ListeningMultiSelect
                        label='Source'
                        allEnumValues={_.mapValues(applicableSources, 'name')}
                        infoEnums={_.mapValues(applicableSources, 'description')}
                        currentValue={selectedSourceIds}
                        onlyEnableOptions={allSelectableSourceIds}
                        countMetricRequest={makeDomainMetricRequest('source', metricFilters, 'source')}
                        onItemSelectionsCommited={onSourceOptionsSelected}
                        otherItems={showedUpsellInitially && sourcesOtherItems}
                        tooltipText={disabledTooltip(`search`, `source`)}
                        trackingId='listening-source-dropdown'
                    />

                    <SentimentMultiSelect
                        sentiment={sentiment}
                        onSentimentOptionsSelected={onSentimentOptionsSelected}
                        trackingId='listening-sentiment-dropdown'
                    />

                    {listeningQueryTypeId !== RivalIQ.enums.listeningQueryTypes.QuidTopic.id &&
                        <LanguageDropdown
                            defaultLanguage={defaultLanguage}
                            onLanguageSelected={onLanguageSelected}
                            isInstantSearch={dashboardType === DashboardTypes.INSTANT_SEARCH}
                            trackingId='listening-language-dropdown'
                        />
                    }
                </div>

                <QueryValidationError
                    searchTermsError={searchWithinError}
                    searchTermsWarning={searchWithinWarning}
                />
            </div>
        </div>
    );
}
